import { ArchiveSortDirection, ArchiveSortOption, ArchiveSortSubject } from '../types'
import { calculateArchiveStats, getArchiveResultsByPuzzleNumber } from '../controllers/archive'
import { DAILY5_START_DATE } from '../config'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { useSettings } from '../hooks/useSettings'
import ArchiveList from './ArchiveList'
import Button from './Button'
import ButtonGroup from './ButtonGroup'
import calculateScore from '../util/calculateScore'
import ChunkyArrowIcon from './Icon/ChunkyArrowIcon'
import formatTime from '../util/formatTime'
import getDateForPuzzleNumber from '../util/getDateForPuzzleNumber'
import StarIcon from './Icon/StarIcon'
import StatRow from './StatRow'

export type ArchivePuzzle = {
	puzzleNumber: number
	puzzleDate: string
	score: number
	time?: number
}

const ArchivePage = () => {
	const settings = useSettings()
	const [, navigate] = useLocation()
	const [sortOption, setSortOption] = useState(settings.archiveSortOption)
	const [sortSubject, sortDirection] = sortOption.split('-') as [
		ArchiveSortSubject,
		ArchiveSortDirection
	]
	const archiveResults = getArchiveResultsByPuzzleNumber()
	const containerRef = useRef(null)

	const {
		completed,
		available,
		completedPercentStr,
		bestTime,
		bestTimeIndex,
		fiveStarGames,
		fiveStarPercentStr,
	} = calculateArchiveStats()

	const puzzles = Array.from({ length: available }, (v, i) => {
		const existingResult = archiveResults[i + 1]
		return {
			puzzleNumber: i + 1,
			puzzleDate: getDateForPuzzleNumber(i + 1, DAILY5_START_DATE),
			score: existingResult ? calculateScore(existingResult.time) : 0,
			time: existingResult ? existingResult.time : undefined,
		}
	})

	const nextUnattemptedPuzzle = puzzles.find(puzzle => puzzle.score === 0)

	// If they've attempted all puzzles, pick a random puzzle
	const nextPuzzleNumber =
		nextUnattemptedPuzzle?.puzzleNumber ?? Math.floor(Math.random() * available)

	function changeSortSubject(newSortSubject: ArchiveSortSubject) {
		updateArchiveSort(`${newSortSubject}-${sortDirection}`)
	}

	function changeSortDirection(newSortDirection: ArchiveSortDirection) {
		updateArchiveSort(`${sortSubject}-${newSortDirection}`)
	}

	function updateArchiveSort(newSortOption: ArchiveSortOption) {
		setSortOption(newSortOption)
		settings.setArchiveSortOption(newSortOption)
	}

	useEffect(() => {
		disableBodyScroll(containerRef.current)
		return () => enableBodyScroll(containerRef.current)
	}, [])

	return (
		<div className="archive__page" ref={containerRef}>
			<div className="archive__welcome">
				<p>
					<strong>Welcome to the Archive</strong>
				</p>
				<p>
					All of our past One&nbsp;Word&nbsp;Search&nbsp;puzzles are here to play at your
					leisure. Playing&nbsp;these&nbsp;will not count towards your regular statistics,
					but&nbsp;they&nbsp;are a marvellous way to pass the time all the same.
				</p>
				<div className="archive-stats-table">
					<StatRow
						label="Completed"
						value={
							<span className="stat-row__value--completed">
								<span>
									{completed} / {available}
								</span>{' '}
								<span>({completedPercentStr})</span>
							</span>
						}
					/>
					{bestTime != null && bestTimeIndex != null && (
						<StatRow
							label="Best Archive Time"
							value={
								<span className="stat-row__value--best-time">
									<span>{formatTime(bestTime)}</span>
									<span>Search #{bestTimeIndex}</span>
								</span>
							}
						/>
					)}
					<StatRow
						label={
							<div className="stat-row__label--stars">
								<StarIcon filled />
								<StarIcon filled />
								<StarIcon filled />
								<StarIcon filled />
								<StarIcon filled />
							</div>
						}
						value={
							<span className="stat-row__value--five-star">
								<span>
									{fiveStarGames} / {available}
								</span>{' '}
								<span>({fiveStarPercentStr})</span>
							</span>
						}
					/>
				</div>

				<p>
					{nextUnattemptedPuzzle
						? 'Your next unattempted puzzle:'
						: 'Here’s a random puzzle to enjoy:'}
				</p>
				<Button
					name="archive-next-play"
					onClick={() => {
						navigate(`/archive/daily/${nextPuzzleNumber}`)
					}}
				>
					Play #{nextPuzzleNumber}
				</Button>
			</div>
			<div className="archive-actions">
				<ButtonGroup
					name="sort-subject"
					onChange={changeSortSubject}
					selectedOptionName={sortSubject}
					options={[
						{
							name: 'number',
							content: 'Number',
						},
						{
							name: 'stars',
							content: 'Stars',
						},
					]}
				/>
				<ButtonGroup
					name="sort-direction"
					onChange={changeSortDirection}
					selectedOptionName={sortDirection}
					options={[
						{
							name: 'asc',
							content: <ChunkyArrowIcon />,
						},
						{
							name: 'desc',
							content: <ChunkyArrowIcon />,
						},
					]}
				/>
			</div>
			<ArchiveList sortOption={sortOption} puzzles={puzzles} />
		</div>
	)
}

export default ArchivePage
